import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { appliedUiScale } from '@helios/utils'
import { debounce } from 'lodash-es'
import { CommentaryEntityContext } from '@helios/common-api'
import {
  CommentaryDeepLinkResult,
  readCommentaryShadowEvent,
} from '../types/cross-shadow-events'
import { CommentsDrawerComponent } from './comments-drawer.component'
import {
  CommentsDrawerPreferences,
  clampPreferences,
  clampToViewport,
  loadPreferences,
  savePreferences,
} from './drawer-preferences'

type ResizeKind = 'left' | 'corner' | null

/**
 * Outer chrome for the comments drawer — handles docked vs. floating mode,
 * drag, resize, and localStorage persistence. Content is delegated to
 * <hs-comments-drawer>. Lives outside <nf-panel> to avoid regressions in
 * the shared Northfork side panel.
 */
@Component({
  selector: 'hs-comments-drawer-frame',
  templateUrl: './comments-drawer-frame.component.html',
  styleUrls: ['./comments-drawer-frame.component.scss'],
})
export class CommentsDrawerFrameComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() isOpen = false
  @Input() entityContext: CommentaryEntityContext | null = null
  @Input() commentaryAction: 'view' | 'add' | null = null
  @Input() commentaryActionSeq = 0
  @Input() accountId: string | null = null
  /** JSON-encoded string[], forwarded to hs-comments-drawer. */
  @Input() relatedAccountIds: string | null = null
  @Input() userId: string | null = null
  @Input() userDisplayName: string | null = null
  /**
   * The thread ids to narrow/jump to — a widget-narrowed slice, or a single
   * id for a comment deep link (DLITE-4369). Forwarded as-is to
   * `hs-comments-drawer`; see threadIdsJson's doc there for the array
   * rationale.
   */
  @Input() threadIds: string[] | null = null
  @Input() threadIdsSeq = 0
  @Input() commentId: string | null = null
  /**
   * DLITE-4369 — controls the `hs-cdf-hidden` CSS class independently of
   * `isOpen`. `isOpen` still gates MOUNTING (so commentary-ui can fetch in
   * the background); this lets a host mount the drawer but keep it visually
   * hidden until it's actually ready to show the deep-linked thread.
   * Defaults to `true` so every other caller (a normal comment-bubble click)
   * is unaffected.
   */
  @Input() isRevealed = true

  @Output() closed = new EventEmitter<void>()
  /** DLITE-4369 — re-emits commentary-ui's `commentary-deep-link-resolved` event, bubbled up from `hs-comments-drawer`. */
  @Output() deepLinkResolved = new EventEmitter<{
    result: CommentaryDeepLinkResult
  }>()

  /**
   * DLITE-4743 — commentary-ui hides its entire root on minimize, with
   * nothing left in-drawer to click to come back. This mirrors that state
   * onto the frame so the frame ALSO goes fully hidden (the same
   * `hs-cdf-hidden` treatment `isRevealed` already uses below) rather than
   * leaving an empty, undraggable rectangle where the drawer used to be.
   * Not persisted — a fresh mount always starts restored, same as
   * commentary-ui's own internal state does. Read by app.component's
   * "Comments" toggle to decide restore vs. open/close; see restore() below
   * for how it calls back down into commentary-ui.
   */
  isMinimized = false

  @ViewChild(CommentsDrawerComponent) drawer?: CommentsDrawerComponent

  prefs: CommentsDrawerPreferences = loadPreferences()

  private dragKind: 'move' | ResizeKind = null
  private dragStartClient = { x: 0, y: 0 }
  private dragScale = 1
  private dragStartPrefs: CommentsDrawerPreferences = this.prefs
  private readonly persistSoon = debounce(() => savePreferences(this.prefs), 80)

  ngAfterViewInit(): void {
    this.snapToViewport()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      // This frame is mounted once per session (gated on a permission flag,
      // not isOpen — see app.component.html), but <commentary-ui> itself
      // mounts/unmounts with hs-comments-drawer's own *ngIf="isVisible", so
      // every open is a FRESH commentary-ui that starts restored. Without
      // this, a minimize → close → reopen leaves the frame's own isMinimized
      // stuck true around a commentary-ui that has already reset itself.
      this.isMinimized = false
    }
    if (changes['isOpen'] && changes['isOpen'].currentValue) {
      this.snapToViewport()
    }
  }

  ngOnDestroy(): void {
    // flush (not cancel) so a resize/drag finishing right before the
    // drawer unmounts still persists the latest size — debounce trailing
    // would otherwise be dropped and the size would revert on next mount.
    this.persistSoon.flush()
  }

  toggleDock(): void {
    this.prefs = clampPreferences({
      ...this.prefs,
      floating: !this.prefs.floating,
    })
    this.snapToViewport()
    this.persistSoon()
  }

  close(): void {
    this.closed.emit()
  }

  /** DLITE-4743 — see isMinimized's own comment for the full round trip. */
  restore(): void {
    // An older commentary-ui has no restore() to call (and so never echoes
    // commentary-drawer-restore), but its own in-drawer restore control is
    // still there once the frame is un-hidden — without this the frame
    // stays hidden with no way back.
    if (!this.drawer?.restore()) this.isMinimized = false
  }

  beginDrag(event: MouseEvent): void {
    if (!this.prefs.floating || event.button !== 0) return
    this.dragKind = 'move'
    this.dragScale = appliedUiScale()
    this.dragStartClient = { x: event.clientX, y: event.clientY }
    this.dragStartPrefs = { ...this.prefs }
    event.preventDefault()
  }

  beginResize(kind: Exclude<ResizeKind, null>, event: MouseEvent): void {
    if (event.button !== 0) return
    this.dragKind = kind
    this.dragScale = appliedUiScale()
    this.dragStartClient = { x: event.clientX, y: event.clientY }
    this.dragStartPrefs = { ...this.prefs }
    event.preventDefault()
    event.stopPropagation()
  }

  // Document-level listeners so a fast drag doesn't strand the state machine
  // when the cursor leaves the frame element.
  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent): void {
    if (this.dragKind == null) return
    // Into the space the prefs are emitted in — see appliedUiScale.
    const dx = (event.clientX - this.dragStartClient.x) / this.dragScale
    const dy = (event.clientY - this.dragStartClient.y) / this.dragScale

    if (this.dragKind === 'move') {
      const { floatingX, floatingY } = clampToViewport(
        {
          ...this.prefs,
          floatingX: this.dragStartPrefs.floatingX + dx,
          floatingY: this.dragStartPrefs.floatingY + dy,
        },
        this.viewport(),
      )
      this.prefs = { ...this.prefs, floatingX, floatingY }
    } else if (this.dragKind === 'left') {
      this.prefs = clampPreferences({
        ...this.prefs,
        dockedWidth: this.dragStartPrefs.dockedWidth - dx,
      })
    } else if (this.dragKind === 'corner') {
      this.prefs = clampToViewport(
        clampPreferences({
          ...this.prefs,
          floatingWidth: this.dragStartPrefs.floatingWidth + dx,
          floatingHeight: this.dragStartPrefs.floatingHeight + dy,
        }),
        this.viewport(),
      )
    }
  }

  @HostListener('document:mouseup')
  onDocumentMouseUp(): void {
    if (this.dragKind == null) return
    this.dragKind = null
    this.persistSoon()
  }

  /**
   * DLITE-4369 — a @HostListener, not a template `(commentary-deep-link-resolved)="..."`
   * binding like the sibling `(commentary-drawer-close)` above: Angular's
   * strictTemplates infers a bare template `$event` as `Event` for
   * non-standard DOM event names, which has no `.detail` and fails a real
   * production build (passes Jest, fails `ng build`). A class-level listener
   * sidesteps that entirely — `event` here is a plain TS parameter, typed by
   * us, not inferred by the template compiler.
   *
   * Found via MR review: the previous version type-asserted `event` as
   * `CustomEvent<{...}>` and read `.detail` unchecked — a stale or
   * mismatched commentary-ui build dispatching this event name with a
   * different/malformed shape would propagate `undefined` (or garbage)
   * straight into app.component's handler and crash it. Routed through
   * readCommentaryShadowEvent, the same guard `commentary-threads-changed`
   * already uses, so a malformed dispatch is silently dropped instead.
   */
  @HostListener('commentary-deep-link-resolved', ['$event'])
  onCommentaryDeepLinkResolvedEvent(event: Event): void {
    const detail = readCommentaryShadowEvent(
      'commentary-deep-link-resolved',
      event,
    )
    if (!detail) return
    this.deepLinkResolved.emit(detail)
  }

  // DLITE-4743 — same composed:true bubbling pattern as
  // commentary-drawer-close above. commentary-ui already hid its own root by
  // the time these fire; this hides the frame around it the same way.
  @HostListener('commentary-drawer-minimize')
  onCommentaryDrawerMinimize(): void {
    this.isMinimized = true
  }

  @HostListener('commentary-drawer-restore')
  onCommentaryDrawerRestore(): void {
    this.isMinimized = false
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.snapToViewport()
  }

  get widthPx(): number {
    return this.prefs.floating
      ? this.prefs.floatingWidth
      : this.prefs.dockedWidth
  }

  get heightStyle(): string | null {
    return this.prefs.floating ? `${this.prefs.floatingHeight}px` : null
  }

  get leftPx(): number | null {
    return this.prefs.floating ? this.prefs.floatingX : null
  }

  get topPx(): number | null {
    return this.prefs.floating ? this.prefs.floatingY : null
  }

  get isDragging(): boolean {
    return this.dragKind != null
  }

  get dockButtonLabel(): string {
    return this.prefs.floating ? 'Dock to right edge' : 'Pop out'
  }

  get dockButtonIcon(): string {
    return this.prefs.floating ? 'mdi-pin' : 'mdi-open-in-new'
  }

  private viewport(): { width: number; height: number } {
    // Bounds local-px prefs, so it has to be in their space; otherwise the
    // floating drawer cannot be dragged into the right or bottom of the screen.
    // Reuses the cached value mid-drag: this is reached from mousemove via
    // clampToViewport, and reading the custom property there forces a style
    // recalc every frame.
    const scale = this.dragKind != null ? this.dragScale : appliedUiScale()
    return {
      width: (typeof window !== 'undefined' ? window.innerWidth : 1280) / scale,
      height:
        (typeof window !== 'undefined' ? window.innerHeight : 800) / scale,
    }
  }

  private snapToViewport(): void {
    const clamped = clampPreferences(this.prefs)
    const { floatingX, floatingY } = clampToViewport(clamped, this.viewport())
    this.prefs = { ...clamped, floatingX, floatingY }
  }
}
